/**
 * Centralized resource clients for every backend CRUD endpoint we expose.
 * Add a new entry here when adding a new module so module code never has
 * to construct a `new CrudService(...)` itself.
 */

import { CrudService } from './crud.service';
import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AIModel,
  AIModelAiProvider,
  AIModelProvider,
  Brand,
  BrandAlias,
  Category,
  CommerceCatalog,
  CustomView,
  CustomViewFilter,
  CustomViewWidget,
  Domain,
  GeoLocation,
  LineOfBiz,
  LineOfBizBrand,
  LineOfBizCategory,
  LineOfBizPersona,
  Organization,
  Persona,
  Product,
  Prompt,
  PromptFunnelStage,
  PromptResponse,
  PromptSet,
  PromptSetPrompt,
  ReportInstance,
  ReportInstancePrompt,
  ReportInstanceStats,
  ReportInstanceWeight,
  ReportTemplate,
  ReportTemplatePrompt,
  ReportTemplatePromptSet,
  SearchPhrase,
  Tenant,
  TenantBrand,
  TenantOrganization,
  Topic,
  VisibilityWeight,
} from '../types/data-models';

// ---------------------------------------------------------------------------
// LLM Agent — non-CRUD job endpoints. The agent service lives under
// `/api/v2/llm-agent/*`; the helpers below are thin wrappers so callers
// don't need to touch axios directly.
// ---------------------------------------------------------------------------

export type LlmJobState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface LlmJobStep {
  step: string;
  startedAt: string;
  endedAt?: string;
}

export interface LlmJobStatus<TResult = unknown> {
  jobId: string;
  state: LlmJobState;
  steps: LlmJobStep[];
  progress?: number;
  result?: TResult;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
  metadata?: Record<string, unknown>;
}

export const llmAgent = {
  async getJob<TResult = unknown>(jobId: string): Promise<LlmJobStatus<TResult>> {
    const res = await api.get<OneEnvelope<LlmJobStatus<TResult>>>(
      `/llm-agent/jobs/${jobId}`,
    );
    return res.data.data;
  },
  async cancelJob(jobId: string): Promise<LlmJobStatus> {
    const res = await api.post<OneEnvelope<LlmJobStatus>>(
      `/llm-agent/jobs/${jobId}/cancel`,
    );
    return res.data.data;
  },
};

// ---------------------------------------------------------------------------
// Visibility view — faceted filter options. Non-CRUD endpoint under
// `/api/v2/flat-views/report-instance-filter-vm/options`.
// ---------------------------------------------------------------------------

/** Currently-selected filter values sent to the faceted-options endpoint. */
export interface ReportInstanceFilterSelection {
  reportTemplateId?: number;
  reportInstanceIds?: number[];
  geoLocationIds?: number[];
  aiModelIds?: number[];
  categoryIds?: number[];
  personaIds?: number[];
  stageIds?: number[];
  intentionTypes?: string[];
  promptResponseSentimentBrandIds?: number[];
  sourceContentSentimentBrandIds?: number[];
  promptCustomFieldAs?: string[];
  promptCustomFieldBs?: string[];
  promptCustomFieldCs?: string[];
}

/** An option carrying its display name and legacy id alongside its id. */
export interface FilterOptionIdName<Id extends number | string = number> {
  id: Id;
  idOld: string | null;
  name: string;
}

/** A report-instance option: id plus its legacy id and generate date. */
export interface ReportInstanceOption {
  id: number;
  idOld: string | null;
  reportInstanceGenerateDate: string;
}

/** Distinct option set available for each filter dimension. */
export interface ReportInstanceFilterOptions {
  reportInstanceIds: ReportInstanceOption[];
  geoLocationIds: FilterOptionIdName[];
  aiModelIds: FilterOptionIdName[];
  categoryIds: FilterOptionIdName[];
  personaIds: FilterOptionIdName[];
  stageIds: FilterOptionIdName[];
  intentionTypes: string[];
  scopes: string[];
  languages: string[];
  importances: string[];
  // Both sentiment brand ids resolve to tenant-brand names.
  // A `sourceContentSentimentBrandIds` entry may have a NULL sentiment label.
  promptResponseSentimentBrandIds: FilterOptionIdName[];
  sourceContentSentimentBrandIds: FilterOptionIdName[];
  promptCustomFieldAs: string[];
  promptCustomFieldBs: string[];
  promptCustomFieldCs: string[];
}

// ---------------------------------------------------------------------------
// Report-instance brand analytics — grouped aggregation over the
// `report_instance_flat_brand` flat view. POST
// `/api/v2/flat-views/report-instance-brand-vw/analytics`.
// ---------------------------------------------------------------------------

export type BrandVwAggregationFn = 'count' | 'countDistinct' | 'sum' | 'avg' | 'min' | 'max';

export interface BrandVwFieldFilter {
  /** camelCase column name of `ReportInstanceFlatBrand`. */
  field: string;
  values: Array<string | number>;
}

export interface BrandVwAggregation {
  fn: BrandVwAggregationFn;
  field: string;
  alias?: string;
  /** Optional per-aggregation `field IN (values)` conditions → `FILTER (WHERE …)`. */
  where?: BrandVwFieldFilter[];
}

export interface BrandVwAnalyticsRequest {
  filters?: {
    lineOfBizId?: number | null;
    reportInstanceIds?: number[];
    additional?: BrandVwFieldFilter[];
  };
  groupBy?: string[];
  aggregations: BrandVwAggregation[];
}

/** One result row: the group-by columns plus each aggregation alias. */
export type BrandVwAnalyticsRow = Record<string, string | number | null>;

// ---------------------------------------------------------------------------
// Custom views — widget data sources + generic flat-view analytics used by
// the widget renderer (supports `enrich`, whose response shape differs).
// ---------------------------------------------------------------------------

/** One selectable widget data source: a flat-view analytics endpoint. */
export interface CustomViewDataSource {
  id: string;
  name: string;
  description: string;
  analyticsPath: string;
}

/** A widget analytics row: group-by columns, aggregation aliases, topN arrays. */
export type FlatViewAnalyticsRow = Record<
  string,
  string | number | null | Array<string | number>
>;

/** `enrich: true` side-car: field → id (stringified) → display entity. */
export type FlatViewAnalyticsEntities = Record<
  string,
  Record<string, { id: number; name?: string } & Record<string, unknown>>
>;

export interface FlatViewAnalyticsResult {
  rows: FlatViewAnalyticsRow[];
  entities?: FlatViewAnalyticsEntities;
}

export const resources = {
  // tenant
  tenants: new CrudService<Tenant>('/tenants'),
  linesOfBusiness: new CrudService<LineOfBiz>('/lines-of-business'),
  /** Start the LLM-backed industries analysis job for one LOB. */
  generateLineOfBizIdustry: async (
    lineOfBizId: number,
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/lines-of-business/${lineOfBizId}/generate-idustry`);
    return res.data.data;
  },
  /** Start the LLM-backed competitor-brands research job for one LOB. */
  generateLineOfBizCompetitors: async (
    lineOfBizId: number,
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/lines-of-business/${lineOfBizId}/brands/generate-competitors`);
    return res.data.data;
  },
  /** Start the LLM-backed buyer-categories research job for one LOB. */
  generateLineOfBizCategory: async (
    lineOfBizId: number,
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/line-of-biz-categories/generate-category/${lineOfBizId}`);
    return res.data.data;
  },
  /** Start the LLM-backed buyer-personas research job for one LOB. */
  generateLineOfBizPersona: async (
    lineOfBizId: number,
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/line-of-biz-personas/generate-persona/${lineOfBizId}`);
    return res.data.data;
  },
  /**
   * Start the LLM-backed buyer-prompts research job for one LOB.
   * Optional filter body narrows the LLM context — omit any field
   * (or pass an empty array) to fall back to "send everything".
   */
  generatePrompts: async (
    lineOfBizId: number,
    options?: {
      categoryIds?: number[];
      personaIds?: number[];
      funnelStageIds?: number[];
      intentions?: Array<'BRANDED' | 'UNBRANDED' | 'INFORMATIONAL' | 'COMPARISON'>;
      languages?: string[];
      /** Total prompts to generate. One of 10 | 50 | 100. */
      count?: number;
    },
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/prompts/generate-prompt/${lineOfBizId}`, options ?? {});
    return res.data.data;
  },
  // (line-of-biz brands are nested; use a dedicated path constructor)
  lineOfBizBrandsFor: (lineOfBizId: number) =>
    new CrudService<LineOfBizBrand>(`/lines-of-business/${lineOfBizId}/brands`),
  tenantOrganizations: new CrudService<TenantOrganization>('/tenant-organizations'),
  tenantBrands: new CrudService<TenantBrand>('/tenant-brands'),
  lineOfBizCategories: new CrudService<LineOfBizCategory>('/line-of-biz-categories'),
  lineOfBizPersonas: new CrudService<LineOfBizPersona>('/line-of-biz-personas'),
  // common
  brands: new CrudService<Brand>('/brands'),
  brandAliasesFor: (brandId: number) => new CrudService<BrandAlias>(`/brands/${brandId}/aliases`),
  organizations: new CrudService<Organization>('/organizations'),
  products: new CrudService<Product>('/products'),
  aiModels: new CrudService<AIModel>('/ai-models'),
  aiModelProviders: new CrudService<AIModelProvider>('/ai-model-providers'),
  aiModelAiProviders: new CrudService<AIModelAiProvider>('/ai-model-ai-providers'),
  categories: new CrudService<Category>('/categories'),
  personas: new CrudService<Persona>('/personas'),
  domains: new CrudService<Domain>('/domains'),
  geoLocations: new CrudService<GeoLocation>('/geo-locations'),
  topics: new CrudService<Topic>('/topics'),
  searchPhrases: new CrudService<SearchPhrase>('/search-phrases'),
  visibilityWeights: new CrudService<VisibilityWeight>('/visibility-weights'),
  // prompts
  prompts: new CrudService<Prompt>('/prompts'),
  promptSets: new CrudService<PromptSet>('/prompt-sets'),
  promptSetPromptsFor: (promptSetId: number) =>
    new CrudService<PromptSetPrompt>(`/prompt-sets/${promptSetId}/prompts`),
  promptFunnelStages: new CrudService<PromptFunnelStage>('/prompt-funnel-stages'),
  promptResponses: new CrudService<PromptResponse>('/prompt-responses'),
  // commerce
  commerceCatalogs: new CrudService<CommerceCatalog>('/commerce-catalogs'),
  // report templates
  reportTemplates: new CrudService<ReportTemplate>('/report-templates'),
  reportTemplatePrompts: new CrudService<ReportTemplatePrompt>('/report-template-prompts'),
  reportTemplatePromptSets: new CrudService<ReportTemplatePromptSet>('/report-template-prompt-sets'),
  // report instances
  reportInstances: new CrudService<ReportInstance>('/report-instances'),
  reportInstancePrompts: new CrudService<ReportInstancePrompt>('/report-instance-prompts'),
  reportInstanceWeights: new CrudService<ReportInstanceWeight>('/report-instance-weights'),
  /**
   * Aggregate counts for the report-instance detail view. `tenantId`
   * scopes the request ( '*' → cross-tenant, matching the Reports
   * Monitoring list). Backed by `GET /report-instances/:id/stats`.
   */
  reportInstanceStats: async (
    id: number,
    opts?: { tenantId?: number | '*' },
  ): Promise<ReportInstanceStats> => {
    const res = await api.get<OneEnvelope<ReportInstanceStats>>(
      `/report-instances/${id}/stats`,
      opts?.tenantId !== undefined
        ? { headers: { 'X-Tenant-Id': String(opts.tenantId) } }
        : undefined,
    );
    return res.data.data;
  },
  /**
   * Filter options for the Visibility view. The selected filters
   * (reportInstanceIds, geoLocationIds, aiModelIds, categoryIds, personaIds)
   * are applied together as AND criteria. Backed by
   * `POST /flat-views/report-instance-filter-vm/options`.
   */
  reportInstanceFilterOptions: async (
    selection: ReportInstanceFilterSelection,
  ): Promise<ReportInstanceFilterOptions> => {
    const body: Record<string, Array<string | number>> = {};
    const addList = (key: string, values?: Array<string | number>) => {
      if (values && values.length > 0) body[key] = values;
    };
    addList('reportInstanceIds', selection.reportInstanceIds);
    addList('geoLocationIds', selection.geoLocationIds);
    addList('aiModelIds', selection.aiModelIds);
    addList('categoryIds', selection.categoryIds);
    addList('personaIds', selection.personaIds);
    const res = await api.post<OneEnvelope<ReportInstanceFilterOptions>>(
      '/flat-views/report-instance-filter-vm/options',
      body,
    );
    return res.data.data;
  },
  /**
   * Grouped aggregation over the `report_instance_flat_brand` flat view. Drives
   * the Visibility view's per-AI-model statistics.
   */
  reportInstanceFlatBrandAnalytics: async (
    body: BrandVwAnalyticsRequest,
  ): Promise<BrandVwAnalyticsRow[]> => {
    const res = await api.post<OneEnvelope<BrandVwAnalyticsRow[]>>(
      '/flat-views/report-instance-brand-vw/analytics',
      body,
    );
    return res.data.data;
  },
  // custom views
  customViews: new CrudService<CustomView>('/custom-views'),
  customViewFiltersFor: (customViewId: number) =>
    new CrudService<CustomViewFilter>(`/custom-views/${customViewId}/filters`),
  customViewWidgetsFor: (customViewId: number) =>
    new CrudService<CustomViewWidget>(`/custom-views/${customViewId}/widgets`),
  /** Static catalog of flat-view data sources a widget can query. */
  customViewDataSources: async (): Promise<CustomViewDataSource[]> => {
    const res = await api.get<OneEnvelope<CustomViewDataSource[]>>(
      '/custom-views/data-sources',
    );
    return res.data.data;
  },
  /**
   * Generic flat-view analytics POST used by the custom-view widget
   * renderer. Normalizes the two response shapes (`enrich: true` returns
   * `{ rows, entities }`, otherwise a bare rows array) into one.
   */
  flatViewAnalytics: async (
    view: string,
    body: Record<string, unknown>,
  ): Promise<FlatViewAnalyticsResult> => {
    const res = await api.post<
      OneEnvelope<FlatViewAnalyticsRow[] | FlatViewAnalyticsResult>
    >(`/flat-views/${view}/analytics`, body);
    const data = res.data.data;
    return Array.isArray(data) ? { rows: data } : data;
  },
} as const;
